import { mediaPaths, storeRefusal, uploadThroughStore } from '@core/api/mediaUpload';

import type { ItemCategory, ItemMedia } from './masters';
import { shrinkPhoto } from './shrinkPhoto';

/**
 * Sending a file straight to the object store.
 *
 * <p>
 * The only code in the app that makes a request somewhere other than our own API, and it is
 * deliberately not routed through `apiFetch`: that helper attaches the session, sets our
 * content type and parses our error shape, and every one of those is wrong for a presigned PUT. In
 * particular the session must *not* be attached — the URL is already signed, and sending a bearer
 * token to a third-party host is how credentials leak.
 * </p>
 *
 * <p>
 * `XMLHttpRequest` rather than `fetch`, for one reason: progress. A hundred-megabyte video needs a
 * bar, and `fetch` still has no upload progress event in any browser. This is the exception that
 * justifies the older API.
 * </p>
 */
export async function putToObjectStore(
  uploadUrl: string,
  contentType: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open('PUT', uploadUrl, true);

    // Has to match what was signed, or the store rejects the upload. That is the check standing in
    // for the one our API cannot make, since it never sees the bytes.
    request.setRequestHeader('Content-Type', contentType);

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }

      // The store answers with XML, not our problem+json — `storeRefusal` words its code.
      reject(new Error(storeRefusal(request.status, request.responseText)));
    };

    request.onerror = () =>
      reject(new Error('The file could not be uploaded. Check the connection and try again.'));

    request.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

    signal?.addEventListener('abort', () => request.abort(), { once: true });

    request.send(file);
  });
}

/**
 * Presign, PUT, confirm — against whichever gallery the caller names. The three steps are the
 * core's `uploadThroughStore`, shared with the phone; what is the website's own is only the send,
 * through `XMLHttpRequest` for its progress bar.
 *
 * <p>
 * <strong>The base path is a parameter because the dance is identical everywhere.</strong> A
 * design's gallery, a garment's own, a category's photograph and a booking's condition evidence all
 * follow the same three steps against the same contract; what differs is the route and, for
 * evidence, one extra field.
 * </p>
 */
export function uploadMedia<T>(
  basePath: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  extra?: Record<string, unknown>,
): Promise<T> {
  return uploadThroughStore<T>(
    basePath,
    file,
    (uploadUrl, contentType) => putToObjectStore(uploadUrl, contentType, file, onProgress, signal),
    extra,
  );
}

/*
  The three catalogue uploads below send a photograph shrunk to 1600 pixels (`shrinkPhoto`) — the
  catalogue is looked at, never printed. A booking's condition photographs go through `uploadMedia`
  directly and are sent as taken: they are what a damage dispute is settled on, and whether 1600
  pixels is enough to show a pulled thread is the shop's call, not this file's.
*/

export async function uploadItemMedia(
  itemId: number,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ItemMedia> {
  return uploadMedia<ItemMedia>(
    mediaPaths.design(itemId),
    await shrinkPhoto(file),
    onProgress,
    signal,
  );
}

/**
 * A category's one photograph. Confirming replaces whatever it had, so this answers the category
 * rather than a gallery row.
 */
export async function uploadCategoryPhoto(
  categoryId: number,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ItemCategory> {
  return uploadMedia<ItemCategory>(
    mediaPaths.category(categoryId),
    await shrinkPhoto(file),
    onProgress,
    signal,
  );
}

/** A photograph of one physical garment, as distinct from one of the design. */
export async function uploadUnitMedia(
  unitId: number,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ItemMedia> {
  return uploadMedia<ItemMedia>(
    mediaPaths.garment(unitId),
    await shrinkPhoto(file),
    onProgress,
    signal,
  );
}
